import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface AvailabilitySettingsProps {
  hostId: string;
}

export const AvailabilitySettings = ({ hostId }: AvailabilitySettingsProps) => {
  const [autoAccept, setAutoAccept] = useState(false);
  const [bufferTime, setBufferTime] = useState(30);
  const { toast } = useToast();

  const handleSave = () => {
    toast({
      title: "Settings Saved",
      description: "Your availability preferences have been updated.",
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold mb-4">Availability Preferences</h2>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Auto-accept bookings</Label>
            <p className="text-sm text-gray-500">
              Automatically accept booking requests that fit your availability
            </p>
          </div>
          <Switch
            checked={autoAccept}
            onCheckedChange={setAutoAccept}
          />
        </div>

        <div className="space-y-2">
          <Label>Buffer time between sessions</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={bufferTime}
              onChange={(e) => setBufferTime(Number(e.target.value))}
              min={0}
              max={120}
              className="w-24"
            />
            <span className="text-gray-500">minutes</span>
          </div>
          <p className="text-sm text-gray-500">
            Add a buffer time between consecutive bookings
          </p>
        </div>

        <Button onClick={handleSave} className="w-full">
          Save Settings
        </Button>
      </div>

      <Card className="mt-8">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Clock className="w-8 h-8 text-primary" />
            <div>
              <h3 className="font-medium">Quick Tip</h3>
              <p className="text-sm text-gray-500 mt-1">
                Setting a buffer time helps you prepare between sessions and ensures a better experience for your guests.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};